import './GoogleBrandHealth.scss';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Dfns from 'date-fns/fp';
import { MdOutlineMap, MdOutlineTableRows, MdShowChart } from 'react-icons/md';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import {
  BrandHealthTableInfo,
  GqvChartRow,
  GqvMapRow
} from '@blisspointmedia/bpm-types/dist/BrandHealthMetrics';
import {
  getColorForIndexedVolume,
  getThresholdBinsWithStdDevNonAdjusted
} from '@blisspointmedia/bpm-types/dist/brandHealthMetricsUtils';
import { DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { LineChart } from '../../Components/Charts/LineChart';
import { ChartContainer } from '../../Components/ChartContainer';
import { Design, Placement, Size } from '../../Components/designConstants';
import { Radio } from '../../Components/FormCheck';
import { DmaMap } from '../../Components/DmaMap/DmaMap';
import { DownloadDropdown } from '../../Components/DownloadDropdown/DownloadDropdown';
import { Dropdown } from '../../Components/Dropdown/Dropdown';
import { DropdownToggleType } from '../../Components/Dropdown/dropdown-constants';
import { Img } from '../../Components/Img';
import { LoadingOverlay } from '../../Components/LoadingOverlay';
import { IconSegmentedControlButton } from '../../Components/SegmentedControlButton/IconSegmentedControlButton/IconSegmentedControlButton';
import { SegmentedControlButton } from '../../Components/SegmentedControlButton/SegmentedControlButton/SegmentedControlButton';
import {
  AxesSkeleton,
  PathSkeleton,
  RectSkeleton,
  Skeleton,
  TableSkeleton
} from '../../Components/Skeleton/Skeleton';
import { BPMTable, CellRenderer, SuperTopItemType } from '../../Components/StickyTable';
import { ToggleSwitch } from '../../Components/ToggleSwitch/ToggleSwitch';
import {
  ConvergentColorMap6,
  Neutral300,
  Channel1,
  Channel10a,
  Channel10b,
  Channel11a,
  Channel12b,
  Channel13b,
  Channel15b,
  Channel16b,
  Channel18b,
  Channel19b,
  Channel2,
  Channel3,
  Channel3b,
  Channel4,
  Channel4b,
  Channel5,
  Channel6,
  Channel6b,
  Channel7,
  Channel7b,
  Channel8,
  Channel8b,
  Channel9a,
  Channel9b
} from '../../utils/colors';
import {
  DATE_GROUPING_OPTIONS,
  dateToMddyy,
  DateRange,
  formatDateLabel
} from '../../utils/date-utils';
import { getThresholdBinsWithStdDev } from '../../utils/charts/maps';
import { downloadPNG, exportToExcel } from '../../utils/download-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useMap } from '../../utils/hooks/useData';
import {
  calculateMovingAverage,
  filterGqvChartRows,
  filterGqvMapRows,
  filterTableDataByLabel,
  formatAndRoundNumber,
  formatGqvQueryDataForTimeSeries,
  formatGqvTooltipValue,
  groupFormattedGqvLineChartDataByDate,
  groupGqvMapRowsByDmaCode,
  groupGqvQueryDataByDmaCodeAndQueryLabel,
  returnTitleForGqvChart
} from './brand-health-metrics-gqv-utils';
import { FiltersPanelGoogleBrandHealth } from './FiltersPanelGoogleBrandHealth';
import { GradientLegend } from '../../Components/GradientLegend/GradientLegend';

// Ported verbatim from bpm-frontend's legacy Channel1-4 palette (different
// hex values than tinuiti's own Channel1-4) to keep each brand/category
// term's chart color stable across the migration.
const VISUALIZATION_COLORS = [
  Channel7,
  '#FFC42E',
  Channel6,
  Channel1,
  Channel2,
  Channel3,
  Channel4,
  Channel5,
  Channel8,
  Channel8,
  Channel4,
  Channel3b,
  Channel4b,
  Channel5,
  Channel6b,
  Channel7b,
  Channel8b,
  Channel9b,
  Channel10b,
  Channel9a,
  Channel12b,
  Channel13b,
  Channel7,
  Channel15b,
  Channel16b,
  Channel11a,
  Channel18b,
  Channel19b,
  Channel10a
];

// Ported verbatim from bpm-frontend's DivergentColorMap8 (a 9-entry array
// despite the name) for the Population Adjusted map gradient. tinuiti's own
// DivergentColorMap8 uses different, redesigned colors and would visibly
// change this specific gradient.
const POPULATION_ADJUSTED_COLOR_MAP = [
  '#FF3D3F',
  '#FF8586',
  '#FFA8A8',
  '#FFD7D7',
  '#AEF5FD',
  '#12E0FE',
  '#12E0FE',
  '#00C9E6',
  '#00B2CB'
];

const gqvDataHeaders = [
  {
    name: 'dma_full_name',
    label: 'DMA Full Name',
    flex: 1
  },
  {
    name: 'dma_code',
    label: 'DMA Code',
    flex: 1
  },
  {
    name: 'Absolute Index',
    label: 'Absolute Index',
    flex: 1,
    renderer: (data: BrandHealthTableInfo): string => formatNumber(data['Absolute Index'], 0)
  },
  {
    name: 'Population',
    label: 'Population',
    flex: 1,
    renderer: (data: BrandHealthTableInfo): string => formatNumber(data.Population, 0)
  }
];

interface ExtendedBrandHealthTableInfo extends BrandHealthTableInfo {
  adjustedIndexedVolume: number | null;
}

interface GoogleBrandHealthProps {
  chartData: GqvChartRow[];
  mapData: GqvMapRow[];
  dateRange: DateRange | undefined;
  uniqueLabels: string[];
  uniqueLabelsToSearchTerms: Map<string, string[]>;
  movingAvgDaysGqv: string;
  isLoading: boolean;
}

const CHART_VIEW_SKELETON_PATH_POINTS = [
  [0, 0.3],
  [0.25, 0.7],
  [0.5, 0.2],
  [0.75, 0.9],
  [1, 0.5]
] as const;

const ChartViewSkeleton = ({ chartTypeView }: { chartTypeView: string }) => (
  <Skeleton>
    {chartTypeView === 'showGraph' && (
      <>
        <PathSkeleton points={CHART_VIEW_SKELETON_PATH_POINTS} />
        <AxesSkeleton size={3} />
      </>
    )}
    {chartTypeView === 'showMap' && <RectSkeleton padding={10} rx={8} ry={8} />}
    {chartTypeView === 'showTable' && <TableSkeleton />}
  </Skeleton>
);

export const GoogleBrandHealth = ({
  chartData,
  mapData,
  dateRange,
  uniqueLabels,
  uniqueLabelsToSearchTerms,
  movingAvgDaysGqv,
  isLoading
}: GoogleBrandHealthProps) => {
  const [chartDateGrouping, setChartDateGrouping] = useState('Day');
  const [selectedToggleOptionMap, setSelectedToggleOptionMap] =
    useState<string>('Population Adjusted');
  const [chartTypeView, setChartTypeView] = useState<string>('showGraph');
  const [isMultiAxis, setIsMultiAxis] = useState(true);
  const [currentTableLabel, setCurrentTableLabel] = useState<string>('');
  const [currentMapLabel, setCurrentMapLabel] = useState<string>('');
  const { fiscal_cal: fiscalCal } = useCompanyInfo();

  useEffect(() => {
    if (uniqueLabels.length > 0) {
      setCurrentTableLabel(uniqueLabels[0]);
      setCurrentMapLabel(uniqueLabels[0]);
    }
  }, [uniqueLabels]);

  const [regionMapGoogle, setRegionMapGoogle] = useMap<string, boolean>({
    'Region-All': true,
    Northeast: false,
    South: false,
    Midwest: false,
    West: false
  });

  const filteredChartRows = useMemo(() => {
    if (chartData.length > 0 && regionMapGoogle && dateRange) {
      return filterGqvChartRows(chartData, regionMapGoogle, dateRange);
    }
    return [];
  }, [chartData, regionMapGoogle, dateRange]);

  const filteredMapRows = useMemo(() => {
    if (mapData.length > 0 && regionMapGoogle && dateRange) {
      return filterGqvMapRows(mapData, regionMapGoogle, dateRange);
    }
    return [];
  }, [mapData, regionMapGoogle, dateRange]);

  const tableExcelAndMapData = useMemo(
    () => groupGqvQueryDataByDmaCodeAndQueryLabel(filteredMapRows),
    [filteredMapRows]
  );

  const { timeSeriesDataGqvByRegionAndDate, filteredTableData, filteredMapData } = useMemo(() => {
    if (
      (filteredChartRows.length > 0 || filteredMapRows.length > 0) &&
      (currentTableLabel || currentMapLabel)
    ) {
      const { res: formattedGqvLineChartData, uniqueLabels: formattedUniqueLabels } =
        formatGqvQueryDataForTimeSeries(filteredChartRows);

      const groupByDateGqvData = groupFormattedGqvLineChartDataByDate(
        formattedGqvLineChartData,
        chartDateGrouping as DateGroupingKey,
        fiscalCal
      );
      const movingAverageData = calculateMovingAverage(
        groupByDateGqvData,
        formattedUniqueLabels,
        movingAvgDaysGqv
      );

      const tableDataForLabel = filterTableDataByLabel(filteredMapRows, currentTableLabel);
      const mapDataForLabel = filterTableDataByLabel(filteredMapRows, currentMapLabel);

      return {
        timeSeriesDataGqvByRegionAndDate: movingAverageData,
        filteredTableData: tableDataForLabel,
        filteredMapData: mapDataForLabel
      };
    }
    return {
      timeSeriesDataGqvByRegionAndDate: [],
      filteredTableData: [],
      filteredMapData: []
    };
  }, [
    chartDateGrouping,
    filteredChartRows,
    filteredMapRows,
    movingAvgDaysGqv,
    fiscalCal,
    currentMapLabel,
    currentTableLabel
  ]);

  const {
    formattedTableData,
    absoluteIndexSum,
    populationSum,
    thresholds,
    dmaIndexMap,
    adjustedThresholds,
    dmaAdjustedIndexMap
  } = useMemo(() => {
    if (filteredTableData.length > 0 || filteredMapData.length > 0) {
      const groupedGqvQueryDataByDmaCodeTable = groupGqvMapRowsByDmaCode(filteredTableData);
      const groupedGqvQueryDataByDmaCodeMap = groupGqvMapRowsByDmaCode(filteredMapData);
      const { absoluteIndexSum: indexSum, populationSum: popSum } =
        groupedGqvQueryDataByDmaCodeTable.reduce(
          (acc, item) => {
            acc.absoluteIndexSum += item['Absolute Index'];
            acc.populationSum += item.Population ?? 0;
            return acc;
          },
          { absoluteIndexSum: 0, populationSum: 0 }
        );

      const nonAdjustedThresholds = getThresholdBinsWithStdDevNonAdjusted(
        groupedGqvQueryDataByDmaCodeMap.map(item => item['Absolute Index'])
      );

      const indexMap: Record<string, BrandHealthTableInfo> = {};
      groupedGqvQueryDataByDmaCodeMap.forEach(item => {
        indexMap[item.dma_code] = {
          dma_code: item.dma_code,
          dma_full_name: item.dma_full_name,
          'Absolute Index': item['Absolute Index'],
          Population: item.Population
        };
      });

      const stdDevThresholds = getThresholdBinsWithStdDev(
        groupedGqvQueryDataByDmaCodeMap
          .map(item => item['Absolute Index Adjusted'])
          .filter((value): value is number => value != null)
      );

      const adjustedIndexMap: Record<string, ExtendedBrandHealthTableInfo> = {};
      groupedGqvQueryDataByDmaCodeMap.forEach(item => {
        adjustedIndexMap[item.dma_code] = {
          dma_code: item.dma_code,
          dma_full_name: item.dma_full_name,
          'Absolute Index': item['Absolute Index'],
          Population: item.Population,
          adjustedIndexedVolume: item['Absolute Index Adjusted'] ?? null
        };
      });

      return {
        formattedTableData: groupedGqvQueryDataByDmaCodeTable,
        absoluteIndexSum: indexSum,
        populationSum: popSum,
        thresholds: nonAdjustedThresholds,
        dmaIndexMap: indexMap,
        adjustedThresholds: stdDevThresholds,
        dmaAdjustedIndexMap: adjustedIndexMap
      };
    }
    return {
      formattedTableData: [],
      absoluteIndexSum: 0,
      populationSum: 0,
      thresholds: [],
      dmaIndexMap: {},
      adjustedThresholds: [],
      dmaAdjustedIndexMap: {}
    };
  }, [filteredMapData, filteredTableData]);

  const { renderColor, renderAdjustedColor, renderToolTip, renderAdjustedTooltip } = useMemo(() => {
    if (
      thresholds &&
      thresholds.length > 0 &&
      adjustedThresholds &&
      adjustedThresholds.length > 0 &&
      dmaIndexMap &&
      dmaAdjustedIndexMap
    ) {
      const renderColorFn = (dma_code: string) => {
        if (dmaIndexMap[dma_code] && dmaIndexMap[dma_code]['Absolute Index'] !== undefined) {
          const { 'Absolute Index': absoluteIndex } = dmaIndexMap[dma_code];
          return getColorForIndexedVolume(absoluteIndex, thresholds, ConvergentColorMap6);
        }
        return Neutral300;
      };

      const renderToolTipFn = (dma_code: string) => {
        if (!dmaIndexMap[dma_code]) {
          return (
            <div className='chart-tooltip-map'>
              <div className='header-label-map'>{`N/A (DMA Code: ${dma_code})`}</div>
              <div className='item-list-map'>
                <div className='item-row-map'>
                  <div className='name-map'>Actual Index </div>
                  <div className='value-map'>N/A</div>
                </div>
                <div className='item-row-map'>
                  <div className='name-map'>Population </div>
                  <div className='value-map'>N/A</div>
                </div>
              </div>
            </div>
          );
        }

        const {
          'Absolute Index': absoluteIndex,
          dma_full_name,
          Population
        } = dmaIndexMap[dma_code];
        return (
          <div className='chart-tooltip-map'>
            <div className='header-label-map'>{`${dma_full_name} (DMA Code: ${dma_code})`}</div>
            <div className='item-list-map'>
              <div className='item-row-map'>
                <div className='name-map'>Actual Index </div>
                <div className='value-map'>{formatAndRoundNumber(absoluteIndex)}</div>
              </div>
              <div className='item-row-map'>
                <div className='name-map'>Population </div>
                <div className='value-map'>{formatNumber(Population, 0)}</div>
              </div>
            </div>
          </div>
        );
      };

      const renderAdjustedColorFn = (dma_code: string) => {
        if (
          dmaAdjustedIndexMap[dma_code] &&
          dmaAdjustedIndexMap[dma_code].adjustedIndexedVolume != null
        ) {
          const { adjustedIndexedVolume } = dmaAdjustedIndexMap[dma_code];
          return getColorForIndexedVolume(
            adjustedIndexedVolume,
            adjustedThresholds,
            POPULATION_ADJUSTED_COLOR_MAP
          );
        }
        return Neutral300;
      };

      const renderAdjustedTooltipFn = (dma_code: string) => {
        if (!dmaAdjustedIndexMap[dma_code]) {
          return (
            <div className='chart-tooltip-map'>
              <div className='header-label-map'>{`N/A (DMA Code: ${dma_code})`}</div>
              <div className='item-list-map'>
                <div className='item-row-map'>
                  <div className='name-map'>Actual Index </div>
                  <div className='value-map'>N/A</div>
                </div>
                <div className='item-row-map'>
                  <div className='name-map'>Population </div>
                  <div className='value-map'>N/A</div>
                </div>
                <div className='footer-map'>
                  <div className='footer-label-map'>Pop. Adjusted Index </div>
                  <div className='footer-value-map'>N/A</div>
                </div>
              </div>
            </div>
          );
        }

        const {
          'Absolute Index': absoluteIndex,
          adjustedIndexedVolume,
          dma_full_name,
          Population
        } = dmaAdjustedIndexMap[dma_code];
        return (
          <div className='chart-tooltip-map'>
            <div className='header-label-map'>{`${dma_full_name} (DMA Code: ${dma_code})`}</div>
            <div className='item-list-map'>
              <div className='item-row-map'>
                <div className='name-map'>Actual Index </div>
                <div className='value-map'>{formatAndRoundNumber(absoluteIndex)}</div>
              </div>
              <div className='item-row-map'>
                <div className='name-map'>Population </div>
                <div className='value-map'>{formatNumber(Population, 0)}</div>
              </div>
              <div className='footer-map'>
                <div className='footer-label-map'>Pop. Adjusted Index </div>
                <div className='footer-value-map'>{formatNumber(adjustedIndexedVolume, 2)}</div>
              </div>
            </div>
          </div>
        );
      };

      return {
        renderColor: renderColorFn,
        renderAdjustedColor: renderAdjustedColorFn,
        renderToolTip: renderToolTipFn,
        renderAdjustedTooltip: renderAdjustedTooltipFn
      };
    }
    return {
      renderColor: () => Neutral300,
      renderAdjustedColor: () => Neutral300,
      renderToolTip: () => 'No info for this DMA',
      renderAdjustedTooltip: () => 'No info for this DMA'
    };
  }, [thresholds, adjustedThresholds, dmaIndexMap, dmaAdjustedIndexMap]);

  const mapSvgRef = useRef<SVGSVGElement | null>(null);

  const pngDownloadTable = useCallback(async () => {
    await downloadPNG('.chart-container', `${chartDateGrouping}_Indexed_Google_Query_Volume`);
  }, [chartDateGrouping]);

  const excelDownloadTable = useCallback(() => {
    exportToExcel(
      tableExcelAndMapData,
      `IndexedGoogleQueryVolume_byDMA_${dateRange?.start}_${dateRange?.end}`
    );
  }, [dateRange?.end, dateRange?.start, tableExcelAndMapData]);

  const pngDownloadTimeSeries = useCallback(async () => {
    await downloadPNG('.chart-container', `${chartDateGrouping}_Indexed_Google_Query_Volume`);
  }, [chartDateGrouping]);

  const excelDownloadTimeSeries = useCallback(() => {
    const transformedTimeSeriesDataGqvByRegionAndDate = timeSeriesDataGqvByRegionAndDate.map(
      ({ ReportDate, ...rest }) => ({
        Date: ReportDate,
        ...rest
      })
    );
    exportToExcel(
      transformedTimeSeriesDataGqvByRegionAndDate,
      `IndexedGoogleQueryVolume_OverTime_${dateRange?.start}_${dateRange?.end}`
    );
  }, [dateRange?.end, dateRange?.start, timeSeriesDataGqvByRegionAndDate]);

  const superHeaderGqvTable: SuperTopItemType<string>[] = [{ span: 4, data: currentTableLabel }];

  const totals = {
    'Absolute Index': Number(absoluteIndexSum.toFixed(0)),
    Population: Number(populationSum.toFixed(0))
  };

  const totalsRenderer: CellRenderer<string | number | Element | undefined> = ({
    data,
    style,
    classes
  }) => {
    if (data === undefined) {
      return <div style={style} className={cn(classes)} />;
    }

    return (
      <div style={style} className={cn(classes)}>
        <div className='cell-contents'>
          <div className='cell-contents-header'>TOTAL</div>
          <div className='cell-contents-value'>{formatNumber(Number(data), 0)}</div>
        </div>
      </div>
    );
  };

  return (
    <div className='google-brand-health'>
      <div className='google-brand-health-left'>
        <ChartContainer
          rightActions={
            <div className='google-brand-health-right-actions'>
              {chartTypeView === 'showGraph' && (
                <ToggleSwitch
                  label='Multi-axis'
                  checked={isMultiAxis}
                  onChange={setIsMultiAxis}
                  size={Size.SM}
                />
              )}
              {chartTypeView === 'showMap' && (
                <SegmentedControlButton
                  design={Design.NEUTRAL}
                  size={Size.SM}
                  options={['Actual', 'Population Adjusted']}
                  selectedOption={selectedToggleOptionMap}
                  onChange={setSelectedToggleOptionMap}
                />
              )}
              <IconSegmentedControlButton
                options={[
                  { key: 'showGraph', icon: <MdShowChart />, label: 'graph view' },
                  { key: 'showMap', icon: <MdOutlineMap />, label: 'map view' },
                  { key: 'showTable', icon: <MdOutlineTableRows />, label: 'table view' }
                ]}
                size={Size.SM}
                selectedOption={chartTypeView}
                onChange={setChartTypeView}
              />
              <DownloadDropdown
                size={Size.SM}
                disabled={isLoading}
                onClickOptions={[
                  chartTypeView === 'showGraph' ? excelDownloadTimeSeries : excelDownloadTable,
                  chartTypeView === 'showGraph' ? pngDownloadTimeSeries : pngDownloadTable
                ]}
              />
            </div>
          }
          title={
            <>
              {chartTypeView === 'showGraph' && (
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  size={Size.LG}
                  type={DropdownToggleType.WIDGET_TITLE}
                  value={chartDateGrouping}
                  options={DATE_GROUPING_OPTIONS}
                  onChange={setChartDateGrouping}
                />
              )}
              <div className='title-for-gqv-chart'>{returnTitleForGqvChart(chartTypeView)}</div>
            </>
          }
          titleAfterDashText={
            dateRange
              ? `${dateToMddyy(dateRange.start)} – ${dateToMddyy(dateRange.end)}`
              : undefined
          }
          viewRightActions
        >
          {isLoading && (
            <LoadingOverlay message='Loading Google query data…'>
              <ChartViewSkeleton chartTypeView={chartTypeView} />
            </LoadingOverlay>
          )}
          {!isLoading && chartTypeView === 'showGraph' && (
            <LineChart
              customTooltipHeader={date =>
                formatDateLabel(date, fiscalCal, chartDateGrouping, true)
              }
              data={timeSeriesDataGqvByRegionAndDate}
              lineDataKeys={uniqueLabels.map((label, index) => ({
                name: label,
                dataKey: label,
                subHeader: uniqueLabelsToSearchTerms.get(label),
                ...(isMultiAxis && {
                  yAxisId: index === 0 ? ('left' as const) : ('right' as const)
                })
              }))}
              colorMappings={uniqueLabels.map((row, index) => ({
                name: row,
                color: VISUALIZATION_COLORS[index]
              }))}
              tooltipFormatter={formatGqvTooltipValue}
              tooltipShape='line'
              sortTooltipValues={false}
              usePercentageYAxis={false}
              useLeftYAxis
              customLeftYAxisDomain={[0, 'auto']}
              customRightYAxisDomain={isMultiAxis ? [0, 'auto'] : undefined}
              xAxisDataKey='ReportDate'
              xAxisTickFormatter={date =>
                chartDateGrouping === 'Month'
                  ? Dfns.format('M/yy', Dfns.parseISO(date))
                  : formatDateLabel(date, fiscalCal, chartDateGrouping, false)
              }
            />
          )}
          {!isLoading && chartTypeView === 'showMap' && (
            <div className='geo-map-container'>
              <div className='geo-map-legend-and-map'>
                <GradientLegend
                  title='Legend:'
                  bins={
                    selectedToggleOptionMap === 'Actual'
                      ? thresholds || []
                      : adjustedThresholds || []
                  }
                  useConvergent={selectedToggleOptionMap === 'Actual'}
                />
                <AutoSizer
                  renderProp={({ width = 0, height = 0 }) => (
                    <DmaMap
                      width={width}
                      height={height}
                      renderColor={
                        selectedToggleOptionMap === 'Actual' ? renderColor : renderAdjustedColor
                      }
                      renderTooltip={
                        selectedToggleOptionMap === 'Actual' ? renderToolTip : renderAdjustedTooltip
                      }
                      ref={mapSvgRef}
                      borderColor='white'
                      selectedBorderColor='white'
                    />
                  )}
                />
              </div>
              <div className='label-filter'>
                {uniqueLabels.map(label => (
                  <div key={label} className='label-item'>
                    <div className='item'>
                      <Radio
                        id={`gqv-map-label-${label}`}
                        className='item-button'
                        name='react-tips'
                        value={label}
                        checked={currentMapLabel === label}
                        onChange={() => setCurrentMapLabel(label)}
                        label={label}
                      />
                    </div>
                    <div className='item-sub-header'>
                      {uniqueLabelsToSearchTerms
                        .get(label)
                        ?.map(item => item.toLowerCase())
                        .join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!isLoading && chartTypeView === 'showTable' && (
            <div className='gqv-table-container'>
              <div className='gqv-table'>
                <BPMTable
                  data={formattedTableData}
                  headers={gqvDataHeaders}
                  filterBar={false}
                  superHeaders={superHeaderGqvTable}
                  totals={formattedTableData.length > 0 ? totals : undefined}
                  totalsRenderer={totalsRenderer}
                />
              </div>
              <div className='label-filter'>
                {uniqueLabels.map(label => (
                  <div key={label} className='label-item'>
                    <div className='item'>
                      <Radio
                        id={`gqv-table-label-${label}`}
                        className='item-button'
                        name='react-tips'
                        value={label}
                        checked={currentTableLabel === label}
                        onChange={() => setCurrentTableLabel(label)}
                        label={label}
                      />
                    </div>
                    <div className='item-sub-header'>
                      {uniqueLabelsToSearchTerms
                        .get(label)
                        ?.map(item => item.toLowerCase())
                        .join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartContainer>
      </div>
      <div className='google-brand-health-right'>
        <ChartContainer title='Filters'>
          <FiltersPanelGoogleBrandHealth
            regionMap={regionMapGoogle}
            setRegionMap={setRegionMapGoogle}
          />
        </ChartContainer>
        <div className='signature'>
          <div className='signature-label-and-image-container'>
            <div className='signature-label'>Powered By:</div>
            <div className='signature-image'>
              <Img src='https://cdn.blisspointmedia.com/assets/img/Google.png' />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
